import type { InfoContent } from "@core/components/InfoDialog";

/**
 * The council's own description of the program and its assessment criteria,
 * carried over from the ArcGIS dashboard this replaces.
 *
 * Two editorial changes to the original text:
 *
 *  - The opening navigation paragraph described the ArcGIS layout ("select a
 *    site in the lower right", "use the arrows at the bottom of the chart").
 *    Those controls do not exist here, so it is rewritten for this app.
 *  - Each criterion is marked with whether the map actually colours against
 *    it. Stating a threshold the colouring does not use would leave the two
 *    quietly contradicting each other; `applied: false` says so in place.
 */
export const ABOUT: InfoContent = {
  title: "About this dashboard",

  lede: [
    "Select a monitoring station on the map or in the site list to see its own record. " +
    "Use the Parameter control to switch between measures, and the Surface, Middle and " +
    "Bottom buttons beneath the chart to show or hide each depth layer. Filter to a " +
    "single waterbody with the Waterbody control, which also gives you a permanent link " +
    "to that waterbody.",

    "The Watershed Council conducts Comprehensive Water Quality Monitoring annually with " +
    "a three-year watershed rotation. Parameters monitored through this program include " +
    "temperature, specific conductivity, pH, dissolved oxygen, total phosphorus, total " +
    "nitrogen, and chloride. The assessment criteria below are derived from the United " +
    "States Environmental Protection Agency's ambient water quality recommendations, the " +
    "State of Michigan, and the Huron River Watershed Council. Parameter values vary " +
    "based on waterbody classification, parameter type, and EPA ecoregions and " +
    "subecoregions.",
  ],

  sections: [
    {
      heading: "Temperature",
      body: [
        "Temperature is important to monitor as it is a driving force in many lake " +
        "processes, and is crucial to support the fisheries in Northern Michigan. In " +
        "April, when monitoring for this program is conducted, the maximum average for " +
        "inland lakes is 60 degrees Fahrenheit (15.56 °C), maximum for warm water streams " +
        "is 56 degrees Fahrenheit (13.33 °C), and maximum for cold water streams is 54 " +
        "degrees Fahrenheit (12.22 °C).",
      ],
      criteria: [
        { label: "Inland lakes, April maximum", value: "15.56 °C (60 °F)", applied: false },
        { label: "Warm water streams, April maximum", value: "13.33 °C (56 °F)", applied: false },
        { label: "Cold water streams, April maximum", value: "12.22 °C (54 °F)", applied: false },
      ],
      note:
        "These criteria depend on whether a site is a lake, a warm water stream or a cold " +
        "water stream, and that classification is not recorded in the monitoring base. " +
        "Until it is, temperature is ranked against the other stations shown rather than " +
        "graded against these maxima.",
    },
    {
      heading: "Specific conductivity",
      body: [
        "Conductivity is a measure of the ability of water to conduct an electric current, " +
        "which is dependent on the concentration of charged particles in the water. " +
        "Research shows that conductivity and chloride levels in surface waters are " +
        "indicators of human disturbance in a watershed. Conductivity levels between 150 " +
        "and 800 µS/cm are considered healthy for surface waters in Northern Michigan.",
      ],
      criteria: [{ label: "Healthy range, Northern Michigan surface waters", value: "150 – 800 µS/cm" }],
    },
    {
      heading: "pH",
      body: [
        "pH is a measure of acidity and alkalinity. pH is a limiting factor for the " +
        "presence and distribution of many aquatic organisms. pH levels between 6.0 and " +
        "9.0 are considered healthy for surface waters in Northern Michigan.",
      ],
      criteria: [{ label: "Healthy range, Northern Michigan surface waters", value: "6.0 – 9.0" }],
    },
    {
      heading: "Dissolved oxygen",
      body: [
        "Dissolved oxygen is necessary for the survival of all aquatic organisms. " +
        "Dissolved oxygen can increase when there is turbulence from wind and waves, but " +
        "can only reach deeper waters through mixing processes. Algae and aquatic plants " +
        "also produce oxygen during photosynthesis. A dissolved oxygen level of 7 mg/L is " +
        "required to support cold water fisheries, and 5 mg/L is required to support warm " +
        "water fisheries.",
      ],
      criteria: [
        { label: "Required for cold water fisheries", value: "7 mg/L" },
        { label: "Required for warm water fisheries", value: "5 mg/L" },
      ],
      note: "For this parameter lower values are the concern, not higher ones.",
    },
    {
      heading: "Total phosphorus",
      body: [
        "Total phosphorus (TP) is a limiting nutrient, as it is required by plants and " +
        "algae to grow and reproduce. However, excess nutrients in the water can cause " +
        "many issues, such as algal blooms. The recommended maximum for TP in surface " +
        "waters in subecoregion 50 is 12 µg/L for streams, and 9.69 µg/L for lakes; " +
        "levels for subecoregion 51 are 28.75 µg/L in streams, and 20 µg/L in lakes.",
      ],
      criteria: [
        { label: "Subecoregion 50 — lakes", value: "9.69 µg/L", applied: false },
        { label: "Subecoregion 50 — streams", value: "12 µg/L", applied: false },
        { label: "Subecoregion 51 — lakes", value: "20 µg/L", applied: false },
        { label: "Subecoregion 51 — streams", value: "28.75 µg/L", applied: false },
      ],
      note:
        "These maxima depend on a station's EPA subecoregion, which is not recorded in the " +
        "monitoring base. Until it is, the map grades total phosphorus against the Carlson " +
        "trophic boundaries of 10 and 20 µg/L, which bracket the lake values above.",
    },
    {
      heading: "Total nitrogen",
      body: [
        "Total nitrogen (TN), like phosphorus, contributes to algae and plant growth, and " +
        "excess TN is indicative of a eutrophic ecosystem. The recommended maximum for " +
        "surface waters in subecoregion 50 is 440 µg/L for streams, and 400 µg/L for " +
        "lakes; levels for subecoregion 51 are 710 µg/L for streams, and 810 µg/L for lakes.",
      ],
      criteria: [
        { label: "Subecoregion 50 — lakes", value: "400 µg/L", applied: false },
        { label: "Subecoregion 50 — streams", value: "440 µg/L", applied: false },
        { label: "Subecoregion 51 — lakes", value: "810 µg/L", applied: false },
        { label: "Subecoregion 51 — streams", value: "710 µg/L", applied: false },
      ],
      note:
        "As with phosphorus, these depend on a station's EPA subecoregion. Until that is " +
        "recorded, total nitrogen is ranked against the other stations shown rather than " +
        "graded against these maxima.",
    },
    {
      heading: "Chloride",
      body: [
        "Chloride is a reliable indicator of human activity in a watershed because many " +
        "human activities are associated with chloride (de-icing salts, water softener " +
        "salts and more). Chloride persists over time, as it is not removed by natural " +
        "processes, which can cause many issues for ecosystems. The aquatic maximum value " +
        "for chloride is 320 mg/L.",
      ],
      criteria: [{ label: "Aquatic maximum", value: "320 mg/L" }],
    },
  ],

  footer: [
    "Criteria shown in grey are published by the Council but are not yet driving the map " +
    "colours, because they depend on a classification the monitoring base does not record. " +
    "Where a parameter has no applicable criterion, the map ranks stations against each " +
    "other on a neutral scale and says so — that is a comparison, never a judgement of " +
    "water quality.",
    "Data is read live from the Council's monitoring records. Use Refresh to pull the " +
    "latest.",
    "This dashboard is published as part of Water Watch, a programme of The Downstream " +
    "Project: thedownstreamproject.org/services/water-watch",
  ],
};
